/*eslint-disable no-unused-vars */
/*eslint-disable react-hooks/exhaustive-deps*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Input, InputGroup, Label, Row, Table } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { PaginationControl } from 'react-bootstrap-pagination-control';
import debounce from 'lodash.debounce';
import { t } from 'i18next';

import { CompanyId, SELECTED_LANG } from '../../constants';
import { numberFormatter, formatNumberRTL } from '../../helpers';

import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';

import NoRecords from '../../components/Table/NoRecords';
import SortableHeader from '../../components/SortableHeader';
import LocationService from '../../services/Location';
import MachineService from '../../services/machines';
import LocalstorageService from '../../helpers/localstorage-service';

import './Withdrawal.css';

const HeaderList = [
  {
    title: t('DATE'),
    value: 'createDate',
    style: { minWidth: '100px', width: '12%' },
  },
  {
    title: t('BATCH_LABEL'),
    value: 'Batch',
    style: { minWidth: '90px', width: '10%' },
  },
  {
    title: t('WITHDRAWAL.AMOUNT_LABEL'),
    value: 'Amount',
    style: { minWidth: '100px', width: '12%' },
  },
  {
    title: t('DRAW_BEFORE_FILLING'),
    value: 'DrawBefore',
    style: { minWidth: '100px', width: '12%' },
  },
  {
    title: t('CHARGE_TERMINAL.ADD_BILLS_LABEL'),
    value: 'AddBills',
    style: { minWidth: '100px', width: '12%' },
  },
  {
    title: t('TOTAL'),
    value: 'Total',
    style: { minWidth: '100px', width: '12%' },
  },
  {
    title: t('EMPLOYEE.COUNTED'),
    style: { minWidth: '50px', width: '6%' },
    sortable: false,
  },
  {
    title: t('EMPLOYEE_LOADS.CLEAR_READER'),
    style: { minWidth: '50px', width: '6%' },
    sortable: false,
  },
  {
    title: t('REMARK'),
    value: 'note',
    style: { minWidth: '120px', width: '18%' },
    sortable: false,
  },
];

const PAGE_SIZE_OPTIONS = [8, 10, 12, 15, 25];

/** Parse DD/MM/YYYY date string to Date object (date-only, midnight) */
function parseDDMMYYYY(dateStr) {
  if (!dateStr) return null;
  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;
  return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
}

function Withdrawal({ formData }) {
  const dispatch = useDispatch();
  const params = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [locationData, setLocationData] = useState([]);
  const [sortConfig, setSortConfig] = useState([{ value: 'createDate', asc: false }]);
  const [remarks, setRemarks] = useState([]);

  const companyId = LocalstorageService.getItem(CompanyId);

  useEffect(() => {
    getWithdrawal(1, itemsPerPage, '', sortConfig);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      getWithdrawal(1, itemsPerPage, search, sortConfig);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Fetch remarks when terminalNumber becomes available
  useEffect(() => {
    if (formData?.terminalNumber) {
      fetchRemarks(formData.terminalNumber);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.terminalNumber]);

  async function fetchRemarks(termNum) {
    try {
      const response = await MachineService.getRemarks({
        terminalNumber: termNum,
        remarkId: 0,
        rowStatus: 0,
      });
      const { dataList } = response.data;
      setRemarks(dataList || []);
    } catch (error) {
      console.error('Error fetching remarks:', error);
    }
  }

  /**
   * Match remarks to loads: each remark is attached to the load with the
   * nearest date that is >= the remark's startDate.
   * Returns a map: loadIndex -> array of remark texts.
   */
  const remarkMap = useMemo(() => {
    if (!remarks?.length || !locationData?.length) return {};

    const map = {};

    for (const remark of remarks) {
      if (!remark.startDate) continue;
      const remarkDate = new Date(remark.startDate);
      remarkDate.setHours(0, 0, 0, 0);

      let closestIdx = -1;
      let closestDiff = Infinity;

      for (let i = 0; i < locationData.length; i++) {
        const loadDate = parseDDMMYYYY(locationData[i].date);
        if (!loadDate) continue;
        loadDate.setHours(0, 0, 0, 0);

        if (loadDate >= remarkDate) {
          const diff = loadDate - remarkDate;
          if (diff < closestDiff) {
            closestDiff = diff;
            closestIdx = i;
          }
        }
      }

      if (closestIdx !== -1) {
        if (!map[closestIdx]) map[closestIdx] = [];
        map[closestIdx].push(remark.remark);
      }
    }

    return map;
  }, [locationData, remarks]);

  async function getWithdrawal(pageNo, pageSize, searchTerm, sort) {
    const payload = {
      pageNo: pageNo,
      pageSize: pageSize,
      allList: false,
      sort: sort,
      locationId: params.locationId,
      companyId: companyId,
    };

    if (searchTerm) {
      payload.search = searchTerm;
    }

    dispatch(setLoadingOverlay(true));
    setIsLoading(true);

    try {
      const response = await LocationService.listWithdrawal(payload);
      const { status, message, dataList, totalCount: respTotal } = response.data;

      if (status === 400) {
        toast.error(message);
        return;
      }

      if (dataList && dataList.length) {
        dataList.map((item) => {
          item.formattedAmount = numberFormatter(item.amount);
          item.formattedEmpBal = numberFormatter(item.employeeBalance);
          return item;
        });

        // SP returns totalCount inside each row (COUNT(*) OVER()), backend may return 0
        const actualTotal = respTotal || dataList[0]?.totalCount || dataList[0]?.filteredCount || 0;

        setLocationData(dataList);
        setTotalCount(actualTotal);
      } else {
        setLocationData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching withdrawal data:', error);
      toast.error('שגיאה בטעינת נתונים');
    } finally {
      setIsLoading(false);
      dispatch(setLoadingOverlay(false));
    }
  }

  const handlePageClick = (page) => {
    setCurrentPage(page);
    getWithdrawal(page, itemsPerPage, search, sortConfig);
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setItemsPerPage(newSize);
    setCurrentPage(1);
    getWithdrawal(1, newSize, search, sortConfig);
  };

  const sortBy = (sortObj) => {
    const newSort = sortObj ? [sortObj] : [{ value: 'createDate', asc: false }];
    setSortConfig(newSort);
    getWithdrawal(currentPage, itemsPerPage, search, newSort);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedChangeHandler = useCallback(debounce((e) => {
    setSearch(e.target.value?.trim() || '');
  }, 500), []);

  return (
    <React.Fragment>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Form className="search-filter" onSubmit={(e) => e.preventDefault()}>
          <InputGroup>
            <Input onChange={debouncedChangeHandler} placeholder={t('SEARCH')} />
            <Button color="primary" type="button">
              <i className="ri-search-line"></i>
            </Button>
          </InputGroup>
        </Form>
      </div>

      <div style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
        <Table striped bordered responsive size="sm">
          <thead>
            <tr>
              <SortableHeader
                headers={HeaderList}
                isDisabled={locationData.length === 0}
                sortClick={sortBy}
                showActionCol={false}
              />
            </tr>
          </thead>

          <tbody>
            {locationData.map((item, l0Idx) => {
              // Check if loading was cancelled (rowStatus === -1)
              // Support both string and number comparison
              const isCancelled = item.rowStatus === -1 || item.rowStatus === '-1';
              const isNegative = parseInt(item.addbills) < 0;

              // Use table-danger class like in Bank Withdrawal screen for consistency
              const rowClassName = isCancelled ? 'table-danger' : '';
              const rowStyle = isCancelled ? {backgroundColor: 'rgb(235, 51, 36)', color: 'white'} : {};

              // For cancelled rows show the cancel note; otherwise show matched remarks
              const remarkText = isCancelled
                ? (item.note || '')
                : (remarkMap[l0Idx] ? remarkMap[l0Idx].join(', ') : '');

              return (
              <tr key={l0Idx} className={rowClassName} style={rowStyle}>
                <td>{item.date}</td>
                <td>{item.batch}</td>
                <td>{formatNumberRTL(item.amount, SELECTED_LANG === 'en' ? 'en-US' : 'he-IL')}</td>
                <td>{item.drawBeforeFilling}</td>
                <td>
                  {formatNumberRTL(item.addbills, SELECTED_LANG === 'en' ? 'en-US' : 'he-IL')}
                </td>
                <td>
                  {formatNumberRTL(item.total, SELECTED_LANG === 'en' ? 'en-US' : 'he-IL')}
                </td>
                <td align="center">
                  {item.manuallyCounted ? (
                    <i className="ri-check-fill" style={{ color: isCancelled ? '#ffffff' : '#28a745' }}></i>
                  ) : (
                    <i className="ri-close-fill" style={{ color: isCancelled ? '#ffffff' : (isNegative ? '#343a40' : '#dc3545') }}></i>
                  )}
                </td>
                <td align="center">
                  {item.cleaningCardReader ? (
                    <i className="ri-check-fill" style={{ color: isCancelled ? '#ffffff' : '#28a745' }}></i>
                  ) : (
                    <i className="ri-close-fill" style={{ color: isCancelled ? '#ffffff' : '#dc3545' }}></i>
                  )}
                </td>
                <td>{remarkText}</td>
              </tr>
              );
            })}

            {!isLoading && locationData.length === 0 && <NoRecords colSpan={HeaderList.length} />}
          </tbody>
        </Table>
      </div>

      {/* Pagination and Rows per page */}
      <div className="custom-pagination mt-2 d-flex justify-content-between align-items-center flex-row-reverse">
        {/* Rows per page - RIGHT side in RTL */}
        <div className="d-flex align-items-center gap-2">
          <Label className="mb-0">{t('ROWS_PER_PAGE') || 'שורות לדף'}:</Label>
          <Input
            type="select"
            value={itemsPerPage}
            onChange={handlePageSizeChange}
            style={{ width: '80px' }}
            bsSize="sm"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Input>
        </div>

        {/* Pagination - LEFT side in RTL */}
        <PaginationControl
          page={currentPage}
          total={totalCount}
          limit={itemsPerPage}
          changePage={handlePageClick}
          next={true}
          last={true}
          ellipsis={1}
        />
      </div>
    </React.Fragment>
  );
}

export default Withdrawal;
